import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Stack,
  Chip,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaCrown,
  FaBolt,
  FaStar,
  FaCalendarAlt,
  FaTag,
  FaClock,
  FaTimes,
  FaCreditCard,
  FaShieldAlt,
  FaUser,
} from "react-icons/fa";
import { API_URL } from "../config";
import { toast } from "react-toastify";

const EASE = [0.16, 1, 0.3, 1];

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

// ─── Per-plan style config ────────────────────────────────────────────────────
function planConfig(months) {
  if (months <= 1)
    return {
      icon: <FaBolt size={20} />,
      label: "Starter",
      color: "#0288D1",
      grad: "linear-gradient(135deg,#0288D1,#0ea5e9)",
      bg: "rgba(2,136,209,0.07)",
      border: "rgba(2,136,209,0.18)",
      glow: "rgba(2,136,209,0.25)",
    };
  if (months <= 3)
    return {
      icon: <FaStar size={20} />,
      label: "Popular",
      color: "#7c3aed",
      grad: "linear-gradient(135deg,#7c3aed,#a78bfa)",
      bg: "rgba(124,58,237,0.07)",
      border: "rgba(124,58,237,0.18)",
      glow: "rgba(124,58,237,0.25)",
      badge: true,
    };
  return {
    icon: <FaCrown size={20} />,
    label: "Best Value",
    color: "#d97706",
    grad: "linear-gradient(135deg,#d97706,#f59e0b)",
    bg: "rgba(217,119,6,0.07)",
    border: "rgba(217,119,6,0.18)",
    glow: "rgba(217,119,6,0.25)",
  };
}

// ─── Plan card ────────────────────────────────────────────────────────────────
function PlanCard({ plan, index, onSelect }) {
  const cfg = planConfig(plan.month);

  return (
    <MotionBox
      initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.75, ease: EASE, delay: index * 0.12 }}
      whileHover={{ y: -8, transition: { duration: 0.3, ease: EASE } }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3.5,
          border: `1px solid ${cfg.border}`,
          background: cfg.bg,
          overflow: "hidden",
          position: "relative",
          cursor: "pointer",
          transition: "box-shadow 0.3s",
          "&:hover": { boxShadow: `0 20px 60px ${cfg.glow}` },
        }}
        onClick={() => onSelect(plan)}
      >
        {/* Popular badge */}
        {cfg.badge && (
          <Box
            sx={{
              position: "absolute",
              top: 14,
              right: -10,
              background: cfg.grad,
              color: "#fff",
              px: 2.5,
              py: 0.4,
              borderRadius: "99px 0 0 99px",
              fontSize: "0.65rem",
              fontWeight: 800,
              letterSpacing: "0.8px",
              textTransform: "uppercase",
              boxShadow: `0 4px 12px ${cfg.glow}`,
              zIndex: 2,
            }}
          >
            Most Popular
          </Box>
        )}

        {/* Gradient accent bar */}
        <Box sx={{ height: 3, background: cfg.grad }} />

        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 2.5 }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                background: cfg.grad,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 6px 16px ${cfg.glow}`,
                color: "#fff",
                flexShrink: 0,
              }}
            >
              {cfg.icon}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: "#0c1a2e",
                  fontSize: "1rem",
                  lineHeight: 1.1,
                }}
              >
                {plan.month} Month{plan.month > 1 ? "s" : ""}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#64748b", fontWeight: 600 }}
              >
                {cfg.label}
              </Typography>
            </Box>
          </Stack>

          {/* Price */}
          <Box
            sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${cfg.border}` }}
          >
            <Stack direction="row" alignItems="flex-end" spacing={0.5}>
              <Typography
                sx={{
                  fontWeight: 900,
                  color: cfg.color,
                  fontSize: "2.4rem",
                  lineHeight: 1,
                  letterSpacing: "-1px",
                }}
              >
                {plan.currency_symbol}
                {plan.price}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#94a3b8", fontWeight: 600, pb: 0.6 }}
              >
                / {plan.month} mo
              </Typography>
            </Stack>
            {plan.month > 1 && (
              <Typography
                variant="caption"
                sx={{ color: "#64748b", fontWeight: 600 }}
              >
                ≈ {plan.currency_symbol}
                {(plan.price / plan.month).toFixed(0)} / month
              </Typography>
            )}
            <Typography
              sx={{
                fontWeight: 900,
                color: cfg.color,
                fontSize: "1rem",
                lineHeight: 1,
                letterSpacing: "-1px",
              }}
            >
              Maximum students: {""} <span>{plan.students}</span>
            </Typography>
          </Box>

          {/* Features */}
          <Stack spacing={1} sx={{ mb: 3 }}>
            {[
              "Full CBT access",
              "All past questions",
              plan.month >= 3 ? "Priority support" : "Standard support",
              plan.month >= 6 ? "Advanced analytics" : "Basic analytics",
            ].map((feat, i) => (
              <Stack key={i} direction="row" spacing={1.2} alignItems="center">
                <FaCheckCircle size={12} color={cfg.color} />
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "0.83rem",
                    fontWeight: 500,
                  }}
                >
                  {feat}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {/* CTA */}
          <Button
            fullWidth
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(plan);
            }}
            sx={{
              py: 1.5,
              borderRadius: 2.5,
              fontWeight: 800,
              fontSize: "0.88rem",
              textTransform: "none",
              background: cfg.grad,
              boxShadow: `0 6px 20px ${cfg.glow}`,
              "&:hover": {
                filter: "brightness(0.92)",
                boxShadow: `0 10px 28px ${cfg.glow}`,
                transform: "translateY(-1px)",
              },
              transition: "all 0.25s",
            }}
          >
            Get Started
          </Button>
        </Box>
      </Paper>
    </MotionBox>
  );
}

// ─── Payment Dialog ───────────────────────────────────────────────────────────
function PaymentDialog({ open, onClose, plan, onPay }) {
  if (!plan) return null;
  const cfg = planConfig(plan.month);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
      BackdropProps={{
        sx: { backdropFilter: "blur(8px)", background: "rgba(12,26,46,0.5)" },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.93, y: 20, filter: "blur(8px)" }}
            animate={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ duration: 0.42, ease: EASE }}
          >
            <DialogContent sx={{ p: 0, overflow: "visible" }}>
              <Box
                sx={{
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(24px)",
                  borderRadius: 3.5,
                  border: `1px solid ${cfg.border}`,
                  boxShadow: `0 24px 80px ${cfg.glow}, 0 4px 24px rgba(0,0,0,0.08)`,
                  overflow: "hidden",
                }}
              >
                <Box sx={{ height: 3, background: cfg.grad }} />

                {/* Close */}
                <Box
                  sx={{ position: "absolute", top: 12, right: 12, zIndex: 10 }}
                >
                  <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{
                      background: "rgba(0,0,0,0.05)",
                      color: "#94a3b8",
                      "&:hover": { background: "rgba(0,0,0,0.1)" },
                    }}
                  >
                    <FaTimes size={13} />
                  </IconButton>
                </Box>

                <Box sx={{ px: 3.5, py: 3.5 }}>
                  {/* Icon */}
                  <Stack alignItems="center" sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        mb: 2,
                        background: cfg.bg,
                        border: `1.5px solid ${cfg.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 8px 24px ${cfg.glow}`,
                        color: cfg.color,
                      }}
                    >
                      <FaCreditCard size={26} />
                    </Box>
                    <Chip
                      label="Payment Summary"
                      size="small"
                      sx={{
                        mb: 1.2,
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        background: cfg.bg,
                        color: cfg.color,
                        border: `1px solid ${cfg.border}`,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 900,
                        color: "#0c1a2e",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      Confirm Subscription
                    </Typography>
                  </Stack>

                  {/* Plan details */}
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border: `1px solid ${cfg.border}`,
                      background: cfg.bg,
                      mb: 2.5,
                      overflow: "hidden",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{
                        px: 2.5,
                        py: 1.8,
                        borderBottom: `1px solid ${cfg.border}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          background: cfg.grad,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          flexShrink: 0,
                        }}
                      >
                        {cfg.icon}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            color: "#0c1a2e",
                            fontSize: "0.9rem",
                          }}
                        >
                          {plan.month} Month{plan.month > 1 ? "s" : ""} —{" "}
                          {cfg.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          LiteUpper Premium
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack
                      spacing={0}
                      divider={<Divider sx={{ borderColor: cfg.border }} />}
                    >
                      {[
                        {
                          icon: <FaTag size={12} />,
                          label: "Amount",
                          value: `${plan.currency_symbol}${plan.price}`,
                          bold: true,
                          color: cfg.color,
                        },
                        {
                          icon: <FaCalendarAlt size={12} />,
                          label: "Duration",
                          value: `${plan.month} month${
                            plan.month > 1 ? "s" : ""
                          }`,
                        },
                        {
                          icon: <FaClock size={12} />,
                          label: "Billing",
                          value: "One-time payment",
                        },
                      ].map((row, i) => (
                        <Stack
                          key={i}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ px: 2.5, py: 1.4 }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Box sx={{ color: "#94a3b8" }}>{row.icon}</Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#64748b", fontWeight: 600 }}
                            >
                              {row.label}
                            </Typography>
                          </Stack>
                          <Typography
                            sx={{
                              fontWeight: row.bold ? 900 : 700,
                              color: row.color || "#0c1a2e",
                              fontSize: row.bold ? "1rem" : "0.85rem",
                            }}
                          >
                            {row.value}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>

                  {/* Security note */}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      mb: 2.5,
                      px: 1.5,
                      py: 1.2,
                      borderRadius: 2,
                      background: "rgba(5,150,105,0.05)",
                      border: "1px solid rgba(5,150,105,0.15)",
                    }}
                  >
                    <FaShieldAlt
                      size={12}
                      color="#059669"
                      style={{ flexShrink: 0 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#059669",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    >
                      Secured by Paystack · 256-bit SSL encryption
                    </Typography>
                  </Stack>

                  {/* Actions */}
                  <Stack direction="row" spacing={3.5}>
                    <Button
                      // fullWidth
                      variant="outlined"
                      onClick={onClose}
                      sx={{
                        py: 1.5,
                        borderRadius: 2.5,
                        fontWeight: 700,
                        fontSize: "0.88rem",
                        textTransform: "none",
                        borderColor: "rgba(2,136,209,0.25)",
                        color: "#64748b",
                        "&:hover": {
                          borderColor: "#0288D1",
                          color: "#0288D1",
                          background: "rgba(2,136,209,0.04)",
                        },
                        transition: "all 0.2s",
                      }}
                    >
                      Cancel
                    </Button>
                    <motion.div
                      style={{ flex: 1 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={onPay}
                        sx={{
                          py: 1.5,
                          borderRadius: 2.5,
                          fontWeight: 800,
                          fontSize: "0.88rem",
                          textTransform: "none",
                          background: cfg.grad,
                          boxShadow: `0 6px 20px ${cfg.glow}`,
                          "&:hover": {
                            filter: "brightness(0.92)",
                            boxShadow: `0 10px 28px ${cfg.glow}`,
                          },
                          transition: "all 0.25s",
                        }}
                      >
                        Pay {plan.currency_symbol}
                        {plan.price}
                      </Button>
                    </motion.div>
                  </Stack>
                </Box>
              </Box>
            </DialogContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Dialog>
  );
}

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [publicKey, setPublicKey] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const tokenString = localStorage.getItem("authToken");
    if (tokenString) {
      const token = JSON.parse(tokenString);
      setAuthToken(token);
      fetchProfile(token);
      fetchPlans(token);
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const fetchProfile = async (token) => {
    try {
      const res = await fetch(`${API_URL}/student/profile/`, {
        headers: {
          Authorization: `Bearer ${token.access}`,
          "Content-Type": "application/json",
        },
      });
      const data = await res.json();
      setUserEmail(data.user.email);
      setPublicKey(data.publickey);
    } catch (err) {
      console.error("Profile fetch failed", err);
    }
  };

  const fetchPlans = async (token) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/subscription-plans/`, {
        headers: {
          Authorization: `Bearer ${token.access}`,
          "Content-Type": "application/json",
        },
      });
      const data = await res.json();
      setPlans(data.plans || []);
      setActivePlan(data.active_plan);
    } catch (err) {
      toast.error("Failed to load plans");
      console.error("Plan fetch failed", err);
    } finally {
      setLoading(false);
    }
  };
  console.log(activePlan);

  const createSubscription = async (planId) => {
    const res = await fetch(`${API_URL}/subscribe/${planId}/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken.access}`,
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) {
      const text = await res.text();
      console.log("Subscribe Error:", text);
      throw new Error("Failed to create subscription");
    }

    return res.json();
    // { message, reference, currency }
  };
  const updateUserPlan = async () => {
    const res = await fetch(`${API_URL}/update-user-plan/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken.access}`,
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) {
      const text = await res.text();
      console.log("Update Plan Error:", text);
      throw new Error("Failed to update user plan");
    }

    return res.json();
  };

  const handlePay = async () => {
    try {
      if (!selectedPlan) return;

      // 1️⃣ Create subscription + get reference
      const data = await createSubscription(selectedPlan.id);
      const { reference, currency } = data;

      if (!window.PaystackPop || !window.PaystackPop.setup) {
        toast.error("Payment system not ready. Refresh page.");
        return;
      }

      // 2️⃣ Open Paystack
      const handler = window.PaystackPop.setup({
        key: publicKey.trim(),
        email: userEmail,
        amount: selectedPlan.price * 100,
        currency: currency,
        ref: reference,
        metadata: {
          custom_fields: [],
        },
        callback: function (response) {
          console.log("Payment success:", response);

          // Call async function separately
          handleSuccessfulPayment();
        },

        onClose: function () {
          toast.error("Payment cancelled");
        },
      });

      handler.openIframe();
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    }
  };

  const handleSuccessfulPayment = async () => {
    try {
      await updateUserPlan();

      toast.success("Subscription successful!");
      setModalVisible(false);
      fetchPlans(authToken);
    } catch (err) {
      console.error("Update plan failed:", err);
      toast.error("Payment succeeded but plan update failed. Contact support.");
    }
  };

  useEffect(() => {
    if (authToken) {
      updateUserPlan().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 50%, #e1f5fe 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Ambient orbs */}
      <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <Box
          sx={{
            position: "absolute",
            width: 600,
            height: 600,
            borderRadius: "50%",
            top: -150,
            left: -120,
            background: "rgba(2,136,209,0.08)",
            filter: "blur(100px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 400,
            height: 400,
            borderRadius: "50%",
            bottom: -80,
            right: -80,
            background: "rgba(124,58,237,0.07)",
            filter: "blur(80px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 300,
            height: 300,
            borderRadius: "50%",
            top: "40%",
            left: "60%",
            background: "rgba(217,119,6,0.05)",
            filter: "blur(70px)",
          }}
        />
      </Box>
      {/* Dot grid */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.35,
          pointerEvents: "none",
          backgroundImage:
            "radial-gradient(circle, rgba(2,136,209,0.2) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      {/* ── Hero header ──────────────────────────────────────────────── */}
      <Box
        sx={{
          background:
            "linear-gradient(135deg, #0288D1 0%, #0ea5e9 55%, #7c3aed 100%)",
          py: { xs: 6, md: 9 },
          px: 2,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.05,
            backgroundImage:
              "radial-gradient(circle, #fff 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            pointerEvents: "none",
          }}
        />

        {/* Back button */}
        <MotionBox
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          sx={{
            position: "absolute",
            left: { xs: 16, md: 32 },
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <IconButton
            type={"button"}
            onClick={() => navigate("/insthome")}
            sx={{
              background: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#fff",
              "&:hover": { background: "rgba(255,255,255,0.25)" },
              transition: "all 0.2s",
              zIndex: 10,
            }}
          >
            <FaArrowLeft size={16} />
          </IconButton>
        </MotionBox>

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <MotionBox
            initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.25)",
                mb: 2.5,
              }}
            >
              <FaCrown size={24} color="#fff" />
            </Box>
            <Chip
              label="Institution Plans"
              size="small"
              sx={{
                mb: 1.5,
                display: "flex",
                mx: "auto",
                width: "fit-content",
                fontWeight: 700,
                fontSize: "0.7rem",
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: "#fff",
                letterSpacing: "-0.5px",
                mb: 1,
              }}
            >
              Subscription Plans
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.75)", fontSize: "1rem" }}
            >
              Unlock premium CBT features for your institution
            </Typography>
          </MotionBox>
        </Box>
      </Box>

      <Container
        maxWidth="lg"
        sx={{ position: "relative", zIndex: 1, py: { xs: 5, md: 8 } }}
      >
        {/* ── Loading ──────────────────────────────────────────────────── */}
        {loading && (
          <Stack alignItems="center" spacing={2} sx={{ py: 12 }}>
            <CircularProgress
              sx={{ color: "#0288D1" }}
              size={44}
              thickness={4}
            />
            <Typography sx={{ color: "#64748b", fontWeight: 600 }}>
              Loading plans…
            </Typography>
          </Stack>
        )}

        {/* ── Plan cards ───────────────────────────────────────────────── */}
        {!loading && (
          <Grid
            container
            spacing={3}
            justifyContent="center"
            sx={{ mb: activePlan ? 5 : 0 }}
          >
            {plans.map((plan, i) => (
              <Grid item xs={12} sm={6} md={4} key={plan.id}>
                <PlanCard
                  plan={plan}
                  index={i}
                  onSelect={(p) => {
                    setSelectedPlan(p);
                    setModalVisible(true);
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* ── Active plan card ─────────────────────────────────────────── */}
        {activePlan && (
          <MotionPaper
            initial={{ opacity: 0, y: 32, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.8, ease: EASE }}
            elevation={0}
            sx={{
              borderRadius: 3.5,
              overflow: "hidden",
              border: "1px solid rgba(5,150,105,0.2)",
              boxShadow: "0 12px 48px rgba(5,150,105,0.12)",
            }}
          >
            <Box
              sx={{
                height: 3,
                background: "linear-gradient(90deg, #059669, #10b981, #059669)",
              }}
            />
            <Box
              sx={{
                px: { xs: 3, md: 5 },
                py: { xs: 3, md: 4 },
                background: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(16px)",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ md: "center" }}
                justifyContent="space-between"
                spacing={3}
              >
                <Stack direction="row" spacing={2.5} alignItems="center">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg,rgba(5,150,105,0.12),rgba(16,185,129,0.08))",
                      border: "1.5px solid rgba(5,150,105,0.25)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: "0 8px 24px rgba(5,150,105,0.15)",
                    }}
                  >
                    <FaCheckCircle size={24} color="#059669" />
                  </Box>
                  <Box>
                    <Chip
                      label="Active"
                      size="small"
                      sx={{
                        mb: 0.6,
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        background: "rgba(5,150,105,0.1)",
                        color: "#059669",
                        border: "1px solid rgba(5,150,105,0.2)",
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: "#0c1a2e",
                        fontSize: "1.1rem",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      Current Subscription
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      Your plan is active and running
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  {[
                    {
                      icon: <FaCalendarAlt size={13} />,
                      label: "Plan",
                      value:
                        activePlan.month == null
                          ? "Unlimited"
                          : `${activePlan.month} Month${
                              activePlan.month > 1 ? "s" : ""
                            }`,
                    },
                    {
                      icon: <FaUser size={13} />,

                      label: "Maximum students",
                      value: activePlan.students,
                    },
                    {
                      icon: <FaTag size={13} />,
                      label: "Price",
                      value: `${activePlan.currency_symbol}${activePlan.price}`,
                    },
                    {
                      icon: <FaClock size={13} />,
                      label: "Ends",
                      value: activePlan.end_date
                        ? new Date(activePlan.end_date).toLocaleDateString()
                        : "unlimited",
                    },
                  ].map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        px: 2.5,
                        py: 1.8,
                        borderRadius: 2.5,
                        background: "rgba(5,150,105,0.06)",
                        border: "1px solid rgba(5,150,105,0.14)",
                        textAlign: "center",
                        minWidth: 110,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.8}
                        alignItems="center"
                        justifyContent="center"
                        sx={{ mb: 0.4, color: "#059669" }}
                      >
                        {item.icon}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: "#059669",
                            fontSize: "0.68rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Stack>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: "#0c1a2e",
                          fontSize: "0.9rem",
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Box>
          </MotionPaper>
        )}
      </Container>

      {/* ── Payment dialog ───────────────────────────────────────────────── */}
      <PaymentDialog
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        plan={selectedPlan}
        onPay={handlePay}
      />
    </Box>
  );
};

export default SubscriptionPlans;
